import { useState, useRef, useEffect } from "react";

interface UseDraggableOptions {
  key: string;
}

export function useDraggable(options: UseDraggableOptions) {
  const [position, setPosition] = useState<{ x: number; y: number }>(() => {
    if (typeof window !== "undefined") {
      try {
        const saved = localStorage.getItem(options.key);
        if (saved) {
          return JSON.parse(saved);
        }
      } catch (e) {
        console.warn("Failed to load draggable position:", e);
      }
    }
    return { x: 0, y: 0 };
  });

  const [isDragging, setIsDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0 });
  const currentPosition = useRef(position);
  const containerRef = useRef<HTMLDivElement>(null);
  const bannerRef = useRef<HTMLDivElement>(null);
  const bannerSize = useRef({ width: 0, height: 0 });

  const updateDOM = (x: number, y: number) => {
    if (containerRef.current) {
      containerRef.current.style.setProperty("--drag-x", `${x}px`);
      containerRef.current.style.setProperty("--drag-y", `${y}px`);
      containerRef.current.style.setProperty("--is-detached", y > 2 ? "1" : "0");
    }

    if (bannerRef.current) {
      const isDetachedLocal = y > 2;
      if (isDetachedLocal) {
        bannerRef.current.classList.add("rounded-xl", "border");
        bannerRef.current.classList.remove("rounded-b-xl", "border-x", "border-b");
        bannerRef.current.style.transition =
          "width 300ms cubic-bezier(0.4, 0, 0.2, 1), min-width 300ms cubic-bezier(0.4, 0, 0.2, 1), border-radius 300ms cubic-bezier(0.4, 0, 0.2, 1) 200ms, padding 300ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1)";
      } else {
        bannerRef.current.classList.add("rounded-b-xl", "border-x", "border-b");
        bannerRef.current.classList.remove("rounded-xl", "border");
        bannerRef.current.style.transition =
          "width 300ms cubic-bezier(0.4, 0, 0.2, 1), min-width 300ms cubic-bezier(0.4, 0, 0.2, 1), border-radius 300ms cubic-bezier(0.4, 0, 0.2, 1), padding 300ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1)";
      }
    }
  };

  // Keep currentPosition ref in sync if position state is updated programmatically from outside
  useEffect(() => {
    currentPosition.current = position;
  }, [position]);

  // Sync state to DOM on position change or when not actively dragging
  useEffect(() => {
    if (!isDragging) {
      updateDOM(position.x, position.y);
    }
  }, [position, isDragging]);

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0 && e.type !== "touchstart") return;

    setIsDragging(true);
    dragStart.current = {
      x: e.clientX - currentPosition.current.x,
      y: e.clientY - currentPosition.current.y,
    };

    if (bannerRef.current) {
      const rect = bannerRef.current.getBoundingClientRect();
      bannerSize.current = {
        width: rect.width,
        height: rect.height,
      };
    }

    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDragging) return;

    let newX = e.clientX - dragStart.current.x;
    let newY = e.clientY - dragStart.current.y;

    const { width, height } = bannerSize.current;
    if (width > 0 && height > 0) {
      const padding = 10;

      // Horizontal constraints
      const minX = -window.innerWidth / 2 + 180 + padding;
      const maxX = window.innerWidth / 2 + 180 - width - padding;
      newX = Math.max(minX, Math.min(maxX, newX));

      // Vertical constraints
      const minY = 0;
      const maxY = window.innerHeight - height - padding;
      newY = Math.max(minY, Math.min(maxY, newY));

      // Snapping
      const SNAP_THRESHOLD_Y = 20;
      if (newY < SNAP_THRESHOLD_Y) {
        newY = 0;
      }
    }

    currentPosition.current = { x: newX, y: newY };
    updateDOM(newX, newY);
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    setIsDragging(false);
    e.currentTarget.releasePointerCapture(e.pointerId);

    const finalPos = currentPosition.current;
    setPosition(finalPos);

    try {
      localStorage.setItem(options.key, JSON.stringify(finalPos));
    } catch (err) {
      console.warn("Failed to save draggable position:", err);
    }
  };

  const resetPosition = () => {
    const defaultPos = { x: 0, y: 0 };
    setPosition(defaultPos);
    currentPosition.current = defaultPos;
    updateDOM(0, 0);

    try {
      localStorage.setItem(options.key, JSON.stringify(defaultPos));
    } catch (err) {
      console.warn("Failed to save snapped position:", err);
    }
  };

  return {
    containerRef,
    bannerRef,
    position,
    setPosition,
    isDragging,
    resetPosition,
    dragHandlers: {
      onPointerDown: handlePointerDown,
      onPointerMove: handlePointerMove,
      onPointerUp: handlePointerUp,
    },
  };
}
